// src/pages/Godown/AddGodown.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import godownService from '../../services/godown.service';
import regionService from '../../services/region.service';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const AddGodown = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    manager: '',
    regionIds: [],
  });

  const [regions, setRegions]           = useState([]);
  const [loadingRegions, setLoadingReg] = useState(false);
  const [errors, setErrors]             = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast]               = useState(null);

  const adminName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username
    : 'Admin User';

  useEffect(() => { fetchRegions(); }, []);

const fetchRegions = async () => {
  setLoadingReg(true);
  try {
    const res = await regionService.getAllRegionsGlobal();
    if (res.success && res.data) setRegions(res.data);
  } catch (err) {
    setToast({ message: 'Failed to load regions', type: 'error' });
  } finally {
    setLoadingReg(false);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim())                  e.name      = 'Godown name is required';
    if (!formData.location.trim())              e.location  = 'Location is required';
    if (!formData.regionIds || formData.regionIds.length === 0) {
      e.regionIds = 'At least one region is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const payload = {
        name:      formData.name.trim(),
        location:  formData.location.trim(),
        manager:   formData.manager.trim() || undefined,
        regionIds: formData.regionIds.map(Number),
      };

    

      const res = await godownService.createGodown(payload);
      if (res.success) {
        setToast({ message: 'Godown added successfully!', type: 'success' });
        setTimeout(() => navigate('/godown/view'), 1200);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to add godown',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-godown-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/godown/view">Godowns</a></li>
          <li className="breadcrumb-item active">Add Godown</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Add New Godown</h4>
        <p className="page-subtitle">Create a new godown/warehouse in the system</p>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Admin Name</label>
                <input
                  type="text" className="form-input" value={adminName}
                  readOnly disabled style={{ backgroundColor: '#f7fafc' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Godown Name <span className="required">*</span></label>
                <input
                  type="text" name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name} onChange={handleChange}
                  placeholder="Enter godown name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <MultiSelect
                  label={<>Regions <span className="required">*</span></>}
                  options={regions}
                  selected={formData.regionIds}
                  onChange={(ids) => {
                    setFormData(prev => ({ ...prev, regionIds: ids }));
                    if (errors.regionIds) setErrors(prev => ({ ...prev, regionIds: '' }));
                  }}
                  loading={loadingRegions}
                  placeholder="Select Regions"
                  emptyMsg="No regions available"
                />
                {errors.regionIds && <span className="error-message">{errors.regionIds}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Location <span className="required">*</span></label>
                <input
                  type="text" name="location"
                  className={`form-input ${errors.location ? 'error' : ''}`}
                  value={formData.location} onChange={handleChange}
                  placeholder="Enter location/city"
                />
                {errors.location && <span className="error-message">{errors.location}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Manager Name</label>
                <input
                  type="text" name="manager" className="form-input"
                  value={formData.manager} onChange={handleChange}
                  placeholder="Enter manager name (optional)"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/godown/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Godown...' : 'Add Godown'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGodown;